'use client'

import { Check, X } from 'lucide-react'
import { motion } from 'motion/react'
import Zoom from 'react-medium-image-zoom'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { QuizAttemptAnswer, QuizPlayerMode, QuizQuestion } from './types'

type OptionState = 'idle' | 'selected' | 'correct' | 'wrong'

function getOptionState(
  optionIndex: number,
  correctIndex: number,
  mode: QuizPlayerMode,
  studentPreviewActive: boolean,
  selectedIndex: number | null,
  submittedIndex: number | null,
  reviewAnswer?: QuizAttemptAnswer,
): OptionState {
  if (mode === 'preview' && !studentPreviewActive) {
    return optionIndex === correctIndex ? 'correct' : 'idle'
  }
  if (mode === 'review' && reviewAnswer) {
    if (optionIndex === correctIndex) return 'correct'
    if (optionIndex === reviewAnswer.selectedIndex) return 'wrong'
    return 'idle'
  }
  if (mode === 'review' && !reviewAnswer) {
    return optionIndex === correctIndex ? 'correct' : 'idle'
  }
  if (submittedIndex !== null) {
    if (optionIndex === correctIndex) return 'correct'
    if (optionIndex === submittedIndex) return 'wrong'
    return 'idle'
  }
  if (optionIndex === selectedIndex) return 'selected'
  return 'idle'
}

function getCardClasses(state: OptionState, interactive: boolean): string {
  const base = 'srd:rounded-xl srd:min-h-[80px] srd:transition-colors srd:w-full srd:text-left srd:text-neutral-black'

  const stateClasses: Record<OptionState, string> = {
    idle: 'srd:bg-neutral-white srd:border-b-[6px] srd:border-blue-100',
    selected: 'srd:bg-blue-20 srd:border-b-[2px] srd:border-blue-100',
    correct: 'srd:bg-blue-10 srd:border-b-[4px] srd:border-blue-100',
    wrong: 'srd:bg-error-container',
  }

  const hoverClasses =
    interactive && state === 'idle' ? 'srd:cursor-pointer srd:hover:border-navy-60 srd:hover:bg-navy-10' : ''

  return cn(base, stateClasses[state], hoverClasses)
}

// ── Text option card ──────────────────────────────────────────────────────────
interface TextOptionCardProps {
  text: string
  state: OptionState
  interactive: boolean
  onClick: () => void
}

function TextOptionCard({ text, state, interactive, onClick }: TextOptionCardProps) {
  return (
    <motion.button
      type='button'
      disabled={!interactive}
      onClick={onClick}
      whileHover={interactive && state === 'idle' ? { scale: 1.01 } : undefined}
      whileTap={interactive ? { scale: 0.98 } : undefined}
      animate={{ scale: state === 'selected' ? 1.01 : 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={cn(getCardClasses(state, interactive), 'srd:flex srd:items-center srd:gap-x-3 srd:px-4 srd:py-6')}
    >
      <p className={cn(typographyVariants({ variant: 'label-lg' }), 'srd:flex-1 srd:text-left')}>{text}</p>
      {state === 'correct' && <Check className='srd:shrink-0 srd:size-6 srd:text-blue-100' aria-hidden='true' />}
      {state === 'wrong' && <X className='srd:shrink-0 srd:size-6 srd:text-error' aria-hidden='true' />}
    </motion.button>
  )
}

// ── Image option card ─────────────────────────────────────────────────────────
interface ImageOptionCardProps {
  imageUrl: string
  text?: string | null
  state: OptionState
  interactive: boolean
  onClick: () => void
}

function ImageOptionCard({ imageUrl, text, state, interactive, onClick }: ImageOptionCardProps) {
  return (
    <motion.button
      type='button'
      disabled={!interactive}
      onClick={onClick}
      whileHover={interactive && state === 'idle' ? { scale: 1.01 } : undefined}
      whileTap={interactive ? { scale: 0.98 } : undefined}
      animate={{ scale: state === 'selected' ? 1.01 : 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={cn(getCardClasses(state, interactive), 'srd:flex srd:items-center srd:gap-x-3 srd:px-4 srd:py-6')}
    >
      <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-2 srd:flex-1'>
        {/* Optional text caption */}
        {text && <p className={cn(typographyVariants({ variant: 'label-lg' }), 'srd:text-left srd:flex-1')}>{text}</p>}

        <img
          src={imageUrl}
          alt=''
          draggable={false}
          className='srd:w-auto srd:h-auto srd:max-h-32 srd:object-contain'
        />
      </div>

      {state === 'correct' && <Check className='srd:shrink-0 srd:size-6 srd:text-blue-100' aria-hidden='true' />}
      {state === 'wrong' && <X className='srd:shrink-0 srd:size-6 srd:text-error' aria-hidden='true' />}
    </motion.button>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export interface QuizQuestionDisplayProps {
  question: QuizQuestion
  questionIndex: number
  mode: QuizPlayerMode
  studentPreviewActive: boolean
  selectedIndex: number | null
  submittedIndex: number | null
  reviewAnswer?: QuizAttemptAnswer
  onSelectOption: (index: number) => void
}

export function QuizQuestionDisplay({
  question,
  questionIndex,
  mode,
  studentPreviewActive,
  selectedIndex,
  submittedIndex,
  reviewAnswer,
  onSelectOption,
}: QuizQuestionDisplayProps) {
  const isInteractive = mode !== 'review' && submittedIndex === null && (mode !== 'preview' || studentPreviewActive)

  const hasImageOptions = question.options.some((opt) => !!opt.imageUrl)

  const showFeedback = submittedIndex !== null || (mode === 'review' && !!reviewAnswer)

  const isCorrect =
    submittedIndex !== null
      ? submittedIndex === question.correctAnswerIndex
      : reviewAnswer?.selectedIndex === question.correctAnswerIndex

  const feedbackText = showFeedback
    ? isCorrect
      ? (question.correctFeedback ?? 'Jawaban benar!')
      : (question.incorrectFeedback ?? 'Jawaban kurang tepat.')
    : null

  return (
    <div className='srd:flex srd:flex-col srd:gap-y-6 srd:items-center srd:justify-center srd:text-center srd:w-full srd:text-neutral-white'>
      {/* Question header */}
      {mode === 'preview' && !studentPreviewActive && (
        <p className={cn(typographyVariants({ variant: 'title-lg' }), 'srd:text-grey-100')}>Soal {questionIndex + 1}</p>
      )}

      <div className='srd:flex srd:flex-col srd:gap-y-2 srd:w-full'>
        {question.imageUrl && (
          <Zoom>
            <img
              src={question.imageUrl}
              alt=''
              draggable={false}
              className='srd:w-full srd:max-h-64 srd:object-contain'
            />
          </Zoom>
        )}

        <h2 className={cn(typographyVariants({ variant: 'headline-sm' }))}>{question.question}</h2>

        {question.description && (
          <p className={cn(typographyVariants({ variant: 'body-lg' }))}>{question.description}</p>
        )}
      </div>

      {/* Options */}
      <div className={cn('srd:flex srd:flex-col srd:gap-3 srd:w-full srd:md:grid srd:md:grid-cols-2')}>
        {question.options.map((option, optionIndex) => {
          const state = getOptionState(
            optionIndex,
            question.correctAnswerIndex,
            mode,
            studentPreviewActive,
            selectedIndex,
            submittedIndex,
            reviewAnswer,
          )

          const handleClick = () => {
            if (isInteractive) onSelectOption(optionIndex)
          }

          if (hasImageOptions && option.imageUrl) {
            return (
              <ImageOptionCard
                key={optionIndex}
                imageUrl={option.imageUrl}
                text={option.text}
                state={state}
                interactive={isInteractive}
                onClick={handleClick}
              />
            )
          }

          return (
            <TextOptionCard
              key={optionIndex}
              text={option.text ?? ''}
              state={state}
              interactive={isInteractive}
              onClick={handleClick}
            />
          )
        })}
      </div>

      {/* Feedback box */}
      {feedbackText && (
        <div className='srd:rounded-xl srd:p-3 srd:border srd:flex srd:flex-col srd:gap-y-2 srd:w-full srd:bg-navy-10 srd:border-navy-50 srd:text-navy-100 srd:text-left'>
          <p className={cn(typographyVariants({ variant: 'body-lg' }), 'srd:font-bold')}>Umpan balik</p>
          <p className={cn(typographyVariants({ variant: 'body-lg' }))}>{feedbackText}</p>
        </div>
      )}
    </div>
  )
}
